import { alpha, Chip, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';

// Components
import { BookmarkTypes, IBookmarkPeople } from 'src/model/bookmark';
import { UnknownText } from 'src/utils/constants/config';
import FlagCountry from 'src/components/common/FlagCountry';
import {
  checkCountryHaveState,
  convertToStartCase,
  getCountryByCountryCode,
  renderTextOrDefault,
} from 'src/utils/helpers/stringHelpers';
import LogoCompany from 'src/components/common/LogoCompany';
import BookmarkCardLayout from 'src/features/Bookmark/Common/BookmarkCardLayout';
import { useNavigate } from 'react-router-dom';
import { AuthRouteKeys } from 'src/utils/constants/routes';

interface IPeopleBookmarkProps {
  dataBookmark: IBookmarkPeople;
}

const PeopleBookmarkCard = (props: IPeopleBookmarkProps) => {
  const { dataBookmark } = props;
  const theme = useTheme();
  const navigate = useNavigate();

  const country = useMemo(() => {
    return getCountryByCountryCode(dataBookmark?.country_code || '');
  }, [dataBookmark]);

  const isCountryHaveState = useMemo(() => {
    return checkCountryHaveState(country?.code || '');
  }, [country]);

  const handleClickBookmark = () => {
    navigate(AuthRouteKeys.PeopleDetail.replace(':id?', dataBookmark?.data_uuid || ''));
  };

  return (
    <BookmarkCardLayout data={dataBookmark} bookmarkType={BookmarkTypes.People}>
      <Stack direction="column" gap={1} onClick={handleClickBookmark}>
        <Stack direction="column" alignItems="flex-start">
          <Tooltip placement="top-start" title={renderTextOrDefault(dataBookmark?.name)}>
            <Typography
              variant="subtitle1"
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: '1',
                lineClamp: '1',
                WebkitBoxOrient: 'vertical',
              }}
            >
              {renderTextOrDefault(dataBookmark?.name)}
            </Typography>
          </Tooltip>
          <Typography variant="caption2" color="text.secondary" align="left">
            {convertToStartCase(dataBookmark?.position || UnknownText)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={3}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FlagCountry code={dataBookmark.country_code || ''} colorDefaultFlag={theme.palette.common.white} />
            {isCountryHaveState ? (
              <Typography variant="caption2" color={'text.secondary'}>
                {country.code} {dataBookmark?.jurisdiction ? `- ${convertToStartCase(dataBookmark?.jurisdiction)}` : ''}
              </Typography>
            ) : (
              <Typography variant="caption2" color={'text.secondary'}>
                {convertToStartCase(country.label)}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <LogoCompany logoURL={''} width={16} height={16} />
            <Tooltip placement="top-start" title={(dataBookmark?.company_name || UnknownText).toUpperCase()}>
              <Typography
                variant="caption2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: '1',
                  lineClamp: '1',
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {(dataBookmark?.company_name || UnknownText).toUpperCase()}
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack gap={0.5} direction="row" flexWrap="wrap">
          {dataBookmark?.is_alerts && (
            <Chip
              label="Alerts"
              color="warning"
              size="small"
              variant="filled"
              sx={{
                height: 18,
                color: theme.palette.common.white,
                backgroundColor: theme.palette.warning.main,
                '&:hover': {
                  color: theme.palette.common.white,
                  backgroundColor: theme.palette.warning.main,
                },
              }}
            />
          )}
          {dataBookmark.category.map((item) => (
            <Chip
              key={`category-${dataBookmark.uuid}-${item}`}
              label={item}
              color="secondary"
              size="small"
              sx={{
                '&:hover': {
                  color: theme.palette.secondary.main,
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                },
              }}
            />
          ))}
        </Stack>
      </Stack>
    </BookmarkCardLayout>
  );
};

export default PeopleBookmarkCard;
